// API helpers — single base, axios with credentials so guest cookies stick.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function fmtCurrency(amount, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return `$${amount}`;
  }
}

/**
 * Convert any thrown API error into a single human-readable string.
 * Priority:
 *  1. detail.ra_error.error           — upstream RA error (proxied)
 *  2. detail.ra_error.message         — upstream RA error variant
 *  3. detail.message / detail (str)   — generic backend
 *  4. detail[*].msg (+ loc prefix)    — Pydantic 422 array
 *  5. error.message                   — Axios / network fallback
 *  6. "Something went wrong, please try again."
 */
export function parseApiError(err) {
  const fallback = "Something went wrong, please try again.";
  if (!err) return fallback;

  // Axios shape: err.response.data
  const data = err?.response?.data;
  const detail = data?.detail ?? data;

  // 1 + 2: nested RA upstream
  const raError = detail?.ra_error;
  if (raError && typeof raError === "object") {
    if (typeof raError.error === "string" && raError.error.trim()) return raError.error;
    if (typeof raError.message === "string" && raError.message.trim()) return raError.message;
  }

  // 3a: detail.message
  if (detail && typeof detail.message === "string" && detail.message.trim()) {
    return detail.message;
  }

  // 3b: detail itself is a string
  if (typeof detail === "string" && detail.trim()) return detail;

  // 4: Pydantic 422 — detail is an array of {loc, msg, type}
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        const msg = d?.msg || "invalid value";
        const loc = Array.isArray(d?.loc) ? d.loc.filter((s) => s !== "body").pop() : null;
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }

  // 5: regular Error
  if (typeof err.message === "string" && err.message.trim()) return err.message;

  return fallback;
}

// Group RA availability rows by priceID → derive a "trip" definition + dates.
export function groupTrips(rows) {
  if (!Array.isArray(rows)) return [];
  const byPrice = new Map();
  for (const r of rows) {
    const pid = r.priceID ?? r.priceId;
    if (!byPrice.has(pid)) {
      byPrice.set(pid, {
        priceID: pid,
        name: r.priceDisplayName || r.priceName || r.serviceName,
        serviceName: r.serviceName,
        assetID: r.assetID ?? r.assetId,
        assetName: r.assetName || r.displayName,
        amount: Number(r.amount || 0),
        validFromTime: r.validFromTime,
        validToTime: r.validToTime,
        durationHrs: timeDiffHours(r.validFromTime, r.validToTime),
        dates: [],
        _rows: [],
      });
    }
    const t = byPrice.get(pid);
    t.dates.push(isoDate(r.date));
    t._rows.push(r);
  }
  // de-dup dates per trip
  for (const t of byPrice.values()) {
    t.dates = Array.from(new Set(t.dates)).sort();
  }
  return Array.from(byPrice.values());
}

export function isoDate(value) {
  if (!value) return "";
  const s = String(value);
  // value like "2026-06-22T00:00:00" → "2026-06-22"
  return s.slice(0, 10);
}

function timeDiffHours(from, to) {
  if (!from || !to) return null;
  const [h1, m1] = from.split(":").map(Number);
  const [h2, m2] = to.split(":").map(Number);
  return (h2 * 60 + m2 - (h1 * 60 + m1)) / 60;
}

export function isoStartDateTime(date, time) {
  // "2026-06-22" + "08:00:00" → "2026-06-22T08:00:00Z"
  return `${date}T${time || "08:00:00"}Z`;
}

export function fmtDateLabel(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function nextNDates(n) {
  const today = new Date();
  const start = today.toISOString().slice(0, 10);
  const end = new Date(today.getTime() + n * 86400000).toISOString().slice(0, 10);
  return { start, end };
}
